import React, { useEffect, useState } from "react";
import { api, API } from "../lib/api";
import { toast } from "sonner";
import { Plus, CheckCircle2, RotateCcw, Trash2, Loader2, Calendar as CalendarIcon, Euro, Camera, FileText, Cloud, ChevronDown, ChevronRight, Eye, X, Download, Pencil } from "lucide-react";
import UploadZone from "./UploadZone";
import PhotoAnnotator from "./PhotoAnnotator";
import { usePhotoActions } from "./PhotoActions";
import { InviaInterventoButton } from "./InviaInterventoButton";

const fmt = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(Number(n || 0));
const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT"); } catch { return iso; }
};
const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return iso; }
};

const formatApiError = (err) => err?.response?.data?.detail || err?.message || String(err);

export default function InterventiLista({ cid, user, restricted = false }) {
  const [items, setItems] = useState([]);
  const [squadre, setSquadre] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [neu, setNeu] = useState({ data_intervento: new Date().toISOString().slice(0, 10), descrizione: "", importo: 0, note: "", squadra_id: "" });
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState(null);

  const toggleFiles = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const startEdit = (it) => {
    setEditingId(it.id);
    setEdit({
      data_intervento: (it.data_intervento || "").slice(0, 10),
      descrizione: it.descrizione || "",
      importo: it.importo || 0,
      note: it.note || "",
      squadra_id: it.squadra_id || "",
    });
  };
  const cancelEdit = () => { setEditingId(null); setEdit(null); };
  const saveEdit = async (id) => {
    if (!edit?.data_intervento) { toast.error("Seleziona la data dell'intervento"); return; }
    setBusyId(id);
    try {
      await api.put(`/interventi-lista/${id}`, { ...edit, importo: Number(edit.importo || 0) });
      toast.success("Intervento aggiornato");
      cancelEdit();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/clienti/${cid}/interventi-lista`);
      setItems(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cid]);
  useEffect(() => {
    (async () => { try { const { data } = await api.get("/squadre"); setSquadre(data || []); } catch { /* */ } })();
  }, []);

  const create = async () => {
    if (!neu.data_intervento) { toast.error("Seleziona la data dell'intervento"); return; }
    try {
      await api.post(`/clienti/${cid}/interventi-lista`, {
        ...neu, importo: Number(neu.importo || 0),
      });
      toast.success("Intervento programmato");
      setAdding(false);
      setNeu({ data_intervento: new Date().toISOString().slice(0, 10), descrizione: "", importo: 0, note: "", squadra_id: "" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const close = async (it) => {
    setBusyId(it.id);
    try {
      await api.post(`/interventi-lista/${it.id}/close`);
      toast.success("Intervento chiuso");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const reopen = async (it) => {
    setBusyId(it.id);
    try {
      await api.post(`/interventi-lista/${it.id}/reopen`);
      toast.success("Intervento riaperto");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Eliminare l'intervento del ${fmtDate(it.data_intervento)}?`)) return;
    setBusyId(it.id);
    try {
      await api.delete(`/interventi-lista/${it.id}`);
      toast.success("Intervento eliminato");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusyId(null); }
  };

  const totale = items.reduce((s, it) => s + Number(it.importo || 0), 0);
  const chiusi = items.filter((it) => it.data_chiusura).length;

  return (
    <div className="space-y-4" data-testid="interventi-lista-block">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-extrabold" style={{ fontFamily: "Manrope" }}>Storico Interventi</h2>
          <p className="text-sm" style={{ color: "var(--text-muted)" }} data-testid="interventi-summary">
            {items.length} interventi totali · {chiusi} chiusi · <b>{fmt(totale)}</b> importo cumulato
          </p>
        </div>
        {!restricted && (
          <button onClick={() => setAdding(true)} className="btn-primary text-sm" data-testid="interventi-add-btn">
            <Plus className="w-4 h-4" /> Nuovo intervento
          </button>
        )}
      </div>

      {adding && (
        <div className="card-shell p-4 space-y-3" data-testid="interventi-add-form">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Data intervento</label>
              <input type="date" value={neu.data_intervento} onChange={(e) => setNeu({ ...neu, data_intervento: e.target.value })}
                className="w-full mt-1 px-3 py-2 text-sm rounded-md border outline-none"
                style={{ borderColor: "var(--border)", background: "#fff" }}
                data-testid="interventi-form-date" />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Importo (€)</label>
              <input type="number" step="0.01" value={neu.importo} onChange={(e) => setNeu({ ...neu, importo: e.target.value })}
                className="w-full mt-1 px-3 py-2 text-sm rounded-md border outline-none"
                style={{ borderColor: "var(--border)", background: "#fff" }}
                data-testid="interventi-form-importo" />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Squadra</label>
              <select value={neu.squadra_id} onChange={(e) => setNeu({ ...neu, squadra_id: e.target.value })}
                className="w-full mt-1 px-3 py-2 text-sm rounded-md border outline-none"
                style={{ borderColor: "var(--border)", background: "#fff" }}
                data-testid="interventi-form-squadra">
                <option value="">— Nessuna squadra —</option>
                {squadre.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Descrizione</label>
            <input type="text" value={neu.descrizione} onChange={(e) => setNeu({ ...neu, descrizione: e.target.value })}
              placeholder="Es. sostituzione inverter, manutenzione annuale…"
              className="w-full mt-1 px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="interventi-form-descrizione" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Note</label>
            <textarea value={neu.note} onChange={(e) => setNeu({ ...neu, note: e.target.value })} rows={2}
              className="w-full mt-1 px-3 py-2 text-sm rounded-md border outline-none"
              style={{ borderColor: "var(--border)", background: "#fff" }}
              data-testid="interventi-form-note" />
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setAdding(false)} className="btn-secondary text-xs">Annulla</button>
            <button onClick={create} className="btn-primary text-xs" data-testid="interventi-form-save">Salva intervento</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="card-shell p-6 flex items-center justify-center text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Caricamento…
        </div>
      ) : items.length === 0 ? (
        <div className="card-shell p-8 text-center" data-testid="interventi-empty">
          <CalendarIcon className="w-8 h-8 mx-auto mb-2" style={{ color: "var(--text-muted)" }} />
          <div className="text-sm" style={{ color: "var(--text-muted)" }}>Nessun intervento programmato per questo cliente</div>
        </div>
      ) : (
        <div className="card-shell overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: "var(--surface-muted)" }}>
                <th className="text-left px-4 py-3 text-xs uppercase font-semibold" style={{ color: "var(--text-muted)" }}>Data intervento</th>
                <th className="text-left px-4 py-3 text-xs uppercase font-semibold" style={{ color: "var(--text-muted)" }}>Descrizione</th>
                <th className="text-left px-4 py-3 text-xs uppercase font-semibold" style={{ color: "var(--text-muted)" }}>Squadra</th>
                <th className="text-right px-4 py-3 text-xs uppercase font-semibold" style={{ color: "var(--text-muted)" }}>Importo</th>
                <th className="text-left px-4 py-3 text-xs uppercase font-semibold" style={{ color: "var(--text-muted)" }}>Stato</th>
                <th className="text-left px-4 py-3 text-xs uppercase font-semibold" style={{ color: "var(--text-muted)" }}>Data chiusura</th>
                <th className="text-right px-4 py-3 text-xs uppercase font-semibold" style={{ color: "var(--text-muted)" }}>Azioni</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const closed = !!it.data_chiusura;
                const isOpen = !!expanded[it.id];
                return (
                  <React.Fragment key={it.id}>
                  <tr className="border-t hover:bg-[var(--surface-muted)]" style={{ borderColor: "var(--border)" }} data-testid={`interventi-row-${it.id}`}>
                    <td className="px-4 py-3 font-mono text-xs">{fmtDate(it.data_intervento)}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold truncate max-w-[300px]">{it.descrizione || "—"}</div>
                      {it.note && <div className="text-xs truncate max-w-[300px]" style={{ color: "var(--text-muted)" }}>{it.note}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {it.squadra_name ? (
                        <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: "var(--surface-muted)", color: "var(--text)" }} data-testid={`interventi-squadra-${it.id}`}>
                          {it.squadra_name}
                        </span>
                      ) : <span className="text-xs" style={{ color: "var(--text-muted)" }}>—</span>}
                    </td>
                    <td className="px-4 py-3 text-right font-mono font-semibold">
                      <span className="inline-flex items-center gap-1">
                        <Euro className="w-3 h-3" style={{ color: "var(--text-muted)" }} />
                        {fmt(it.importo).replace("€", "")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {closed ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: "#ecfdf5", color: "#065f46" }} data-testid={`interventi-status-${it.id}`}>
                          <CheckCircle2 className="w-3 h-3" /> Chiuso
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full font-bold" style={{ background: "#fef3c7", color: "#92400e" }} data-testid={`interventi-status-${it.id}`}>
                          Programmato
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs" style={{ color: "var(--text-muted)" }}>
                      {closed ? (
                        <div>
                          <div>{fmtDateTime(it.data_chiusura)}</div>
                          {it.chiuso_by_name && <div className="text-[10px]">da {it.chiuso_by_name}</div>}
                        </div>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex flex-wrap items-center justify-end gap-1">
                        <button onClick={() => toggleFiles(it.id)}
                          className="btn-secondary text-xs"
                          data-testid={`interventi-files-toggle-${it.id}`}
                          title="Foto e documenti dell'intervento (Aruba Drive)">
                          {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                          <Camera className="w-3.5 h-3.5" /> Foto
                        </button>
                        {!restricted && (
                          <button onClick={() => (editingId === it.id ? cancelEdit() : startEdit(it))}
                            className="btn-secondary text-xs"
                            data-testid={`interventi-edit-${it.id}`}
                            title="Modifica intervento (aggiungi info)">
                            <Pencil className="w-3.5 h-3.5" /> Modifica
                          </button>
                        )}
                        {!closed ? (
                          <button onClick={() => close(it)} disabled={busyId === it.id || restricted}
                            className="btn-primary text-xs disabled:opacity-40"
                            data-testid={`interventi-close-${it.id}`}
                            title="Marca l'intervento come chiuso">
                            {busyId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                            Intervento chiuso
                          </button>
                        ) : (
                          <>
                          {["admin", "operatore", "tecnico"].includes(user?.role) && <InviaInterventoButton it={it} onSent={load} />}
                          {!restricted && user?.role !== "tecnico" && (
                            <button onClick={() => reopen(it)} disabled={busyId === it.id}
                              className="btn-secondary text-xs disabled:opacity-40"
                              data-testid={`interventi-reopen-${it.id}`}>
                              <RotateCcw className="w-3.5 h-3.5" /> Riapri
                            </button>
                          )}
                          </>
                        )}
                        {!restricted && user?.role !== "tecnico" && (
                          <button onClick={() => remove(it)} disabled={busyId === it.id}
                            className="p-1.5 rounded hover:bg-red-50 disabled:opacity-40"
                            style={{ color: "#dc2626" }}
                            title="Elimina"
                            data-testid={`interventi-delete-${it.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {editingId === it.id && (
                    <tr data-testid={`interventi-edit-row-${it.id}`}>
                      <td colSpan={7} className="px-4 py-4" style={{ background: "var(--surface-muted)" }}>
                        <div className="space-y-3">
                          <div className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1" style={{ color: "var(--text-muted)" }}>
                            <Pencil className="w-3.5 h-3.5" /> Modifica intervento
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Data intervento</label>
                              <input type="date" value={edit.data_intervento} onChange={(e) => setEdit({ ...edit, data_intervento: e.target.value })}
                                className="w-full mt-1 px-3 py-2 text-sm rounded-md border outline-none"
                                style={{ borderColor: "var(--border)", background: "#fff" }}
                                data-testid={`interventi-edit-date-${it.id}`} />
                            </div>
                            <div>
                              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Importo (€)</label>
                              <input type="number" step="0.01" value={edit.importo} onChange={(e) => setEdit({ ...edit, importo: e.target.value })}
                                className="w-full mt-1 px-3 py-2 text-sm rounded-md border outline-none"
                                style={{ borderColor: "var(--border)", background: "#fff" }}
                                data-testid={`interventi-edit-importo-${it.id}`} />
                            </div>
                            <div className="md:col-span-2">
                              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Squadra</label>
                              <select value={edit.squadra_id} onChange={(e) => setEdit({ ...edit, squadra_id: e.target.value })}
                                className="w-full mt-1 px-3 py-2 text-sm rounded-md border outline-none"
                                style={{ borderColor: "var(--border)", background: "#fff" }}
                                data-testid={`interventi-edit-squadra-${it.id}`}>
                                <option value="">— Nessuna squadra —</option>
                                {squadre.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                              </select>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div className="md:col-span-4">
                              <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Descrizione</label>
                              <input type="text" value={edit.descrizione} onChange={(e) => setEdit({ ...edit, descrizione: e.target.value })}
                                className="w-full mt-1 px-3 py-2 text-sm rounded-md border outline-none"
                                style={{ borderColor: "var(--border)", background: "#fff" }}
                                data-testid={`interventi-edit-descrizione-${it.id}`} />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>Note</label>
                            <textarea value={edit.note} onChange={(e) => setEdit({ ...edit, note: e.target.value })} rows={2}
                              className="w-full mt-1 px-3 py-2 text-sm rounded-md border outline-none"
                              style={{ borderColor: "var(--border)", background: "#fff" }}
                              data-testid={`interventi-edit-note-${it.id}`} />
                          </div>
                          <div className="flex justify-end gap-2">
                            <button onClick={cancelEdit} className="btn-secondary text-xs" data-testid={`interventi-edit-cancel-${it.id}`}>Annulla</button>
                            <button onClick={() => saveEdit(it.id)} disabled={busyId === it.id} className="btn-primary text-xs disabled:opacity-50" data-testid={`interventi-edit-save-${it.id}`}>
                              {busyId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />} Salva modifiche
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                  {isOpen && (
                    <tr data-testid={`interventi-files-row-${it.id}`}>
                      <td colSpan={7} className="px-4 py-4" style={{ background: "var(--surface-muted)" }}>
                        <InterventoFiles iid={it.id} restricted={restricted} />
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function InterventoFiles({ iid, restricted = false }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [annotating, setAnnotating] = useState(null);

  const tokenParam = () => {
    const t = localStorage.getItem("access_token");
    return t ? `?auth=${encodeURIComponent(t)}` : "";
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/interventi-lista/${iid}/files`);
      setFiles(data || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [iid]);

  const handleUpload = async (fs) => {
    for (const f of fs) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post(`/interventi-lista/${iid}/files`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setFiles((prev) => [...prev, data]);
      } catch (err) {
        toast.error(`${f.name}: ${formatApiError(err)}`);
      }
    }
    toast.success("File caricati · sincronizzazione Aruba Drive in corso");
  };

  const removeFile = async (f) => {
    if (!window.confirm(`Eliminare ${f.original_filename}?`)) return;
    try {
      await api.delete(`/files/${f.id}`);
      setFiles((prev) => prev.filter((x) => x.id !== f.id));
      toast.success("File eliminato");
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const srcOf = (f) => { const tp = tokenParam(); return `${API}/files/${f.id}/download${tp}${tp ? "&" : "?"}v=${f.size || 0}`; };
  const photos = usePhotoActions({
    items: files.filter((f) => (f.content_type || "").startsWith("image/")).map((f) => ({ f, src: srcOf(f) })),
    canEdit: !restricted, onEdit: setAnnotating, onDelete: removeFile,
  });

  return (
    <div className="space-y-3" data-testid={`intervento-files-${iid}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
        <Camera className="w-3.5 h-3.5" /> Foto e documenti · salvati su Aruba Drive
      </div>

      {!restricted && <UploadZone onFiles={handleUpload} />}

      {loading ? (
        <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>
      ) : files.length === 0 ? (
        <div className="text-xs" style={{ color: "var(--text-muted)" }} data-testid={`intervento-files-empty-${iid}`}>Nessun file per questo intervento.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
          {files.map((f) => {
            const isImage = (f.content_type || "").startsWith("image/");
            const isPdf = (f.content_type || "") === "application/pdf";
            const tp = tokenParam();
            const src = `${API}/files/${f.id}/download${tp}${tp ? "&" : "?"}v=${f.size || 0}`;
            return (
              <div key={f.id} className="relative group rounded-lg border overflow-hidden"
                style={{ borderColor: "var(--border)", background: "#fff" }}
                data-testid={`intervento-file-${f.id}`}>
                {isImage && (
                  <button type="button" onClick={() => photos.open(f.id)} className="block w-full" data-testid={`intervento-photo-thumb-${f.id}`}>
                    <img src={src} alt={f.original_filename} className="w-full h-24 object-cover" loading="lazy" />
                  </button>
                )}
                {isPdf && (
                  <button type="button" onClick={() => setPdfPreview({ src, name: f.original_filename })}
                    className="block w-full h-24 relative overflow-hidden bg-[var(--surface-muted)]"
                    data-testid={`intervento-pdf-thumb-${f.id}`} title="Apri anteprima PDF">
                    <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
                      <FileText className="w-7 h-7" style={{ color: "var(--primary)" }} />
                      <span className="text-[10px] uppercase font-semibold">PDF</span>
                    </div>
                    <div className="absolute bottom-1 right-1"><Eye className="w-3.5 h-3.5" style={{ color: "var(--text-muted)" }} /></div>
                  </button>
                )}
                {!isImage && !isPdf && (
                  <a href={src} target="_blank" rel="noreferrer" className="flex flex-col items-center justify-center h-24 bg-[var(--surface-muted)] gap-1">
                    <FileText className="w-7 h-7" style={{ color: "var(--primary)" }} />
                    <span className="text-[10px] uppercase">File</span>
                  </a>
                )}
                <div className="p-1.5 text-[11px] truncate flex items-center gap-1" title={f.original_filename}>
                  {f.nextcloud_path && <Cloud className="w-3 h-3 shrink-0" style={{ color: "#10B981" }} title="Sincronizzato su Aruba Drive" />}
                  <span className="truncate">{f.original_filename}</span>
                </div>
                {!restricted && (
                  <button onClick={() => removeFile(f)}
                    className="absolute top-1.5 right-1.5 p-1 rounded-md bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`intervento-file-delete-${f.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {!restricted && isImage && (
                  <button onClick={() => setAnnotating(f)}
                    className="absolute top-1.5 right-9 p-1 rounded-md bg-black/70 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`intervento-file-annotate-${f.id}`}
                    title="Disegna sulla foto">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {pdfPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-3"
          onClick={() => setPdfPreview(null)} data-testid="intervento-pdf-preview-modal">
          <div className="bg-white rounded-xl w-full max-w-5xl h-[92vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "var(--border)" }}>
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-4 h-4 shrink-0" style={{ color: "var(--primary)" }} />
                <span className="text-sm font-semibold truncate" title={pdfPreview.name}>{pdfPreview.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <a href={pdfPreview.src} download={pdfPreview.name} className="btn-secondary text-xs"><Download className="w-3.5 h-3.5" /> Scarica</a>
                <button onClick={() => setPdfPreview(null)} className="p-1.5 rounded hover:bg-black/5"><X className="w-4 h-4" /></button>
              </div>
            </div>
            <iframe src={pdfPreview.src} title={pdfPreview.name} className="flex-1 w-full" style={{ border: 0 }} />
          </div>
        </div>
      )}

      {photos.ui}
      {annotating && (
        <PhotoAnnotator
          fileId={annotating.id}
          fileName={annotating.original_filename}
          onClose={() => setAnnotating(null)}
          onSaved={(doc) => setFiles((prev) => prev.map((x) => (x.id === doc.id ? doc : x)))}
        />
      )}
    </div>
  );
}
